// @fileoverview : 기준정보 > 로케이션

import { mutateVehicles } from "@/api/vehicle/useVehicleQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { Filter } from "@/features/standardLayout/Filter";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { SearchBox } from "@/features/standardLayout/SearchBox";
import { Sorter } from "@/features/standardLayout/Sorter";
import { ActionHeader } from "@/features/standardLayout/StandardInfoActionHeader";
import {
  flexGrowable,
  flexStable,
  StandardLayoutTableWrapper,
} from "@/features/standardLayout/Styles";
import customAlert from "@/features/ui/alert/Alert";
import { ConfirmForm } from "@/features/ui/confirm";
import { VehicleCreateForm } from "@/features/vehicle/form/CreateForm";
import { VehicleDetailForm } from "@/features/vehicle/form/DetailForm";
import { VehicleUpdateForm } from "@/features/vehicle/form/UpdateForm";
import { handleErrorResponse } from "@/utils/errorMessage";
import { ActionIcon, Avatar, Flex, Text, Tooltip } from "@mantine/core";
import {
  ScmApiVehiclesVehicleIdDeleteRequest,
  VehiclesGet200Response,
  VehiclesGet200ResponseRowsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconEdit, IconFilterPlus, IconTrash } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useMemo } from "react";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";

export const VehiclesPage = () => {
  const { openModal, closeModal } = useModal();
  const { selectedRows, refetch } = useStandardLayout();
  const { t } = useTranslation();

  const { mutate: deleteMutate } = useMutation(
    (params: ScmApiVehiclesVehicleIdDeleteRequest) =>
      mutateVehicles
        .delete(params)
        .mutationFn(params as ScmApiVehiclesVehicleIdDeleteRequest | any),
    {
      onSuccess: () => {
        refetch();
        customAlert(
          t("message.deleteSuccess", { item: t("vehicle.vehicle") }),
          t("common.deleteSuccess"),
          "green",
        );
      },
      onError: (error) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("vehicle.code") }),
          t("common.deleteFail", { item: t("vehicle.vehicle") }),
          "red",
        );
      },
    },
  );

  const columns: readonly Column<VehiclesGet200ResponseRowsInner>[] = useMemo(
    () => [
      {
        ...SelectColumn,
        resizable: true,
      },
      {
        key: "code",
        name: t("vehicle.code"),
        sortable: true,
        resizable: true,
        formatter: ({ row }: { row: VehiclesGet200ResponseRowsInner }) => {
          return (
            <LinkAnchor
              onClick={() => {
                openModal(<VehicleDetailForm code={row.code} />, null, "");
              }}
            >
              {row.code}
            </LinkAnchor>
          );
        },
      },
      {
        key: "name",
        name: t("vehicle.name"),
        sortable: true,
        resizable: true,
      },
      {
        key: "plateNumber",
        name: t("vehicle.plateNumber"),
        sortable: true,
        resizable: true,
      },
      {
        key: "driver",
        name: t("vehicle.driver"),
        sortable: true,
        resizable: true,
      },
      {
        key: "options",
        name: t("common.action"),
        sortable: true,
        resizable: true,
        formatter: ({ row }: { row: any }) => {
          const updateLocationActionView = () => {
            openModal(
              <VehicleUpdateForm formatterProps={row} />,
              null,
              t("common.edit", { item: t("vehicle.vehicle") }),
              true,
            );
          };

          const deleteLocationAction = () => {
            openModal(
              <ConfirmForm
                message={t("message.rowDelete")}
                yesCallback={() => deleteMutate({ vehicleId: row.id as number })}
                noCallback={() => {}}
                commonCallback={() => closeModal(null)}
              />,
              null,
              t("common.delete", { item: t("vehicle.vehicle") }),
            );
          };

          return (
            <Flex w="100%" h="100%" justify="center" align="center" gap="md">
              <ActionIcon variant="subtle" onClick={updateLocationActionView} color="yellow">
                <IconEdit />
              </ActionIcon>
              <ActionIcon variant="subtle" onClick={() => deleteLocationAction()} color="red">
                <IconTrash />
              </ActionIcon>
            </Flex>
          );
        },
      },
    ],
    [deleteMutate, openModal, closeModal, t],
  );

  const handleDelete = () => {
    if ([...selectedRows].length === 0) {
      customAlert(
        t("message.deleteSelect", { item: t("vehicle.vehicle") }),
        t("common.deleteFail", { item: t("vehicle.vehicle") }),
        "red",
      );
      return;
    }

    openModal(
      <ConfirmForm
        message={t("message.rowDelete")}
        yesCallback={() => {
          [...selectedRows].forEach((row) => {
            deleteMutate({ vehicleId: JSON.parse(row).id });
          });

          selectedRows.clear();
        }}
        noCallback={() => {}}
        commonCallback={() => closeModal(null)}
      />,
      null,
      t("common.delete", { item: t("vehicle.vehicle") }),
    );
  };

  return (
    <Flex w="100%" h="100%" gap="xs" direction="column">
      <Flex p="sm" justify="space-between" rowGap="md">
        <ActionHeader
          createFn={() =>
            openModal(
              <VehicleCreateForm />,
              null,
              t("common.additional", { item: t("vehicle.vehicle") }),
              true,
            )
          }
          createDataTitle={t("vehicle.vehicle")}
          deleteFn={() => handleDelete()}
        />
      </Flex>
      {
        <Flex px="sm" direction="column" gap="xs">
          <Text fz="xl" fw={600}>
            {t("common.list", { item: t("vehicle.vehicle") })}
          </Text>
          <Text fz="xs">{t("common.subTitle", { item: t("vehicle.vehicle") })}</Text>
        </Flex>
      }
      <Flex justify="space-between" align="flex-start" px="sm">
        <Flex wrap="wrap" styles={flexGrowable}>
          <Flex gap="xs" align="center" h="100%" wrap="wrap" direction="row">
            <Tooltip label={t("common.filterDescription")}>
              <Avatar color="blue" variant="outline">
                <IconFilterPlus />
              </Avatar>
            </Tooltip>
            <Flex direction="column" gap="xs" wrap="wrap">
              <Filter />
              <Sorter />
            </Flex>
          </Flex>
        </Flex>
        <Flex styles={flexStable}>
          <SearchBox />
        </Flex>
      </Flex>
      <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
        <StandardLayoutTableWrapper>
          <ListTable<VehiclesGet200Response, VehiclesGet200ResponseRowsInner>
            columns={columns}
            rowKeyGetter={(row: VehiclesGet200ResponseRowsInner) =>
              JSON.stringify({ id: row.id, code: row.code })
            }
          />
        </StandardLayoutTableWrapper>
        <PaginationBox<VehiclesGet200Response> />
      </Flex>
    </Flex>
  );
};
